import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StatsResponse, StatsDisplay } from '../../models/mailarchiv/stats.model';
import { DatePipe } from '@angular/common';
import { ApiEnvelope } from '../../helpers/api-envelope.helper';

@Injectable({
  providedIn: 'root',
})
export class StatsService {
  constructor(
    private http: HttpClient,
    private datePipe: DatePipe
  ) {}

  getStats(): Observable<StatsDisplay> {
    return this.http.get<StatsResponse | ApiEnvelope<StatsResponse>>(environment.apiUrl + '/stats').pipe(
      map((res) => this.mapToDisplay('data' in res ? res.data : res))
    );
  }

  /**
   * Fetches stats for a single mail account (admin or user with access to that account).
   */
  getStatsForAccount(mailAccountId: number): Observable<StatsDisplay> {
    return this.http
      .get<StatsResponse | ApiEnvelope<StatsResponse>>(environment.apiUrl + '/stats', {
        params: { mail_account_id: mailAccountId },
      })
      .pipe(map((res) => this.mapToDisplay('data' in res ? res.data : res)));
  }

  private mapToDisplay(res: StatsResponse): StatsDisplay {
    const dict = res.stats?._IndexStats__dict ?? res.stats;
    const numberOfDocuments =
      typeof dict === 'object' && dict !== null && 'numberOfDocuments' in dict
        ? (dict as { numberOfDocuments: number }).numberOfDocuments
        : 0;

    const oldestHit = res.oldest_mail?.hits?.[0];
    const newestHit = res.newest_mail?.hits?.[0];
    const lastImportHit = res.last_import?.hits?.[0];
    const account = lastImportHit?.Account ?? null;

    const oldestMailDate =
      oldestHit?.Date_Unix != null
        ? (this.datePipe.transform(
            oldestHit.Date_Unix * 1000,
            environment.displayDateTimeFormat
          ) ?? null)
        : oldestHit?.Date ?? null;

    const newestMailDate =
      newestHit?.Date_Unix != null
        ? (this.datePipe.transform(
            newestHit.Date_Unix * 1000,
            environment.displayDateTimeFormat
          ) ?? null)
        : newestHit?.Date ?? null;

    const lastImportInfo =
      lastImportHit?.ID != null
        ? (this.datePipe.transform(
            lastImportHit.ID * 1000,
            environment.displayDateTimeFormat
          ) ?? null)
        : lastImportHit?.CreatedAt != null
          ? (this.datePipe.transform(
              lastImportHit.CreatedAt,
              environment.displayDateTimeFormat
            ) ?? null)
          : null;

    return {
      numberOfDocuments,
      oldestMailDate,
      newestMailDate,
      lastImportInfo,
      account,
    };
  }
}
